/**
 * Site Plan Overlay Plugin for Search Results Map
 *
 * This plugin manages the lifecycle of georeferenced site plan image overlays on the map.
 * It responds to data changes (via Redux store) and map viewport changes (zoom, bounds)
 * to incrementally render/hide overlays with zero full-page re-renders.
 *
 * Design:
 * - Renderer: Manages overlay attach/detach/sync via facade, keyed by SitePlanId
 * - Debounce: rAF-based single-slot queue coalesces rapid updates (zoom, data, bounds)
 * - Store integration: Subscribes to Redux sitePlans + mapResults observables, no direct mutations
 * - Viewport filtering: Only renders overlays within current map bounds
 * - Results filtering: Detaches overlays for communities no longer in map results
 * - Zoom gate: Only renders when zoom >= 14 (per product requirements)
 * - Camera state read fresh from getCameraSnapshot() at render time so store-triggered
 *   renders (e.g. results changing while the map is stationary) never use stale values
 *
 * @module searchResultsMap/plugins/sitePlan.plugin
 */

import { map, distinctUntilChanged } from 'rxjs';
import { selectSitePlansById, selectMapResultCommunityIds } from '../redux/selectors';
import fetchSitePlansEpic from '../redux/fetchSitePlansEpic';
import cleanupSitePlansEpic from '../redux/cleanupSitePlansEpic';
import { isPolygonInBounds, hasCameraChanged } from '../services/geometry';
import { SITE_PLAN_CLASS_NAMES } from '../services/sitePlanBehavior';
import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from '../constants';

/**
 * Creates a renderer that manages overlay lifecycles.
 *
 */
function createRenderer(mapFacade, eventHandlers) {
  const overlays = new Map();

  /**
   * Attaches a site plan overlay to the map.
   *
   * @param {Object} sitePlan - Site plan data
   */
  function attachOverlay(sitePlan) {
    const overlay = mapFacade.createImageBoundsOverlay(sitePlan, eventHandlers);
    if (!overlay) {
      return;
    }
    overlay.attach();
    overlays.set(String(sitePlan.sitePlanId), overlay);
  }

  /**
   * Detaches a single overlay by SitePlanId.
   *
   */
  function detachOverlay(sitePlanId) {
    const overlay = overlays.get(sitePlanId);
    if (!overlay) {
      return;
    }
    overlay.detach();
    overlays.delete(sitePlanId);
  }

  /**
   * Removes all overlays from the map.
   */
  function detachAllOverlays() {
    overlays.forEach((overlay) => overlay.detach());
    overlays.clear();
  }

  /**
   * Updates overlay visibility and data based on current zoom, viewport, and active results.
   * Performs incremental attach/detach/syncâ€”no full re-renders.
   *
   * @param {Object} sitePlans - keyed by sitePlanId
   * @param {Set<number>} resultCommunityIds - communityIds currently present in map results
   * @param {number} zoom
   * @param {{ south: number, north: number, west: number, east: number }|null} bounds
   */
  function update(sitePlans, sitePlanValues, resultCommunityIds, zoom, bounds) {
    if (zoom < SITE_PLAN_RENDER_ZOOM_THRESHOLD) {
      detachAllOverlays();
      return;
    }

    if (resultCommunityIds.size === 0) {
      detachAllOverlays();
      return;
    }

    // Single-pass loop avoids the intermediate arrays produced by .filter().map().
    const nextVisibleIds = new Set();
    sitePlanValues.forEach((sitePlan) => {
      if (
        !sitePlan.isInteractiveSitePlan &&
        resultCommunityIds.has(sitePlan.communityId) &&
        isPolygonInBounds(sitePlan, bounds)
      ) {
        nextVisibleIds.add(String(sitePlan.sitePlanId));
      }
    });

    // Collect keys to detach without spread to avoid an extra array allocation.
    const idsToDetach = [];
    overlays.forEach((_, id) => {
      if (!nextVisibleIds.has(id)) {
        idsToDetach.push(id);
      }
    });
    idsToDetach.forEach(detachOverlay);

    nextVisibleIds.forEach((sitePlanId) => {
      const sitePlan = sitePlans[sitePlanId];
      if (!sitePlan) {
        return;
      }

      if (overlays.has(sitePlanId)) {
        overlays.get(sitePlanId).syncData(sitePlan);
      } else {
        attachOverlay(sitePlan);
      }
    });
  }

  return { update, detachAllOverlays };
}

/**
 * Creates the Site Plan plugin for the plugin host.
 *
 * Plugin lifecycle:
 * - init: Initializes state
 * - start: Sets up store subscription, map event listeners, renderer
 * - stop: Cleans up all subscriptions, listeners, and overlays
 *
 */
export default function createSitePlanPlugin({ onSitePlanClick, onSitePlanHover, onSitePlanLeave } = {}) {
  const state = {
    started: false,
    renderer: null,
    subscriptions: [],
    mapEventUnsubscribers: [],
    sitePlans: {},
    sitePlanValues: [],
    resultCommunityIds: new Set(),
    store$: null,
    mapFacade: null,
    requestFrameId: null,
    lastRenderedSnapshot: null,
  };

  /**
   * Schedules a debounced render via rAF.
   * Coalesces multiple rapid updates (data, zoom, bounds) into one cycle.
   * Zoom and bounds are read fresh from getCameraSnapshot() at render time so
   * store-triggered renders never depend on stale cached camera state.
   */
  function scheduleRender() {
    if (state.requestFrameId !== null) {
      return;
    }
    state.requestFrameId = requestAnimationFrame(() => {
      state.requestFrameId = null;
      const snapshot = state.mapFacade?.getCameraSnapshot();
      state.lastRenderedSnapshot = snapshot ?? null;
      state.renderer?.update(
        state.sitePlans,
        state.sitePlanValues,
        state.resultCommunityIds,
        snapshot?.zoom ?? 0,
        snapshot?.bounds ?? null,
      );
    });
  }

  /**
   * Schedules a render only when the camera has moved since the last render.
   * Used exclusively by the idle handler to avoid redundant update() calls
   * when idle fires without an actual viewport change.
   */
  function scheduleRenderOnCameraChange() {
    const snapshot = state.mapFacade?.getCameraSnapshot();
    if (!hasCameraChanged(state.lastRenderedSnapshot, snapshot ?? null)) {
      return;
    }
    scheduleRender();
  }

  return {
    name: 'site-plan',

    async init(context) {
      context.services.epic$.next(fetchSitePlansEpic);
      context.services.epic$.next(cleanupSitePlansEpic);
      context.state.sitePlan = { started: false, ready: false };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      const mapFacade = context.services.mapFacade;
      if (!mapFacade) {
        return;
      }

      state.store$ = context.services.store$;

      const eventHandlers = {
        onSitePlanClick,
        onSitePlanHover,
        onSitePlanLeave,
        classNames: SITE_PLAN_CLASS_NAMES,
      };

      state.renderer = createRenderer(mapFacade, eventHandlers);
      state.mapFacade = mapFacade;

      state.subscriptions.push(
        state.store$.pipe(
          map(selectSitePlansById),
          distinctUntilChanged()
        ).subscribe((data) => {
          state.sitePlans = data;
          state.sitePlanValues = Object.values(data);
          scheduleRender();
        })
      );

      state.subscriptions.push(
        state.store$.pipe(
          map(selectMapResultCommunityIds),
          distinctUntilChanged()
        ).subscribe((ids) => {
          state.resultCommunityIds = ids;
          scheduleRender();
        })
      );

      const unsubscribeBounds = mapFacade.onMapEvent('idle', () => {
        scheduleRenderOnCameraChange();
      });

      state.mapEventUnsubscribers.push(unsubscribeBounds);
      state.started = true;
      context.state.sitePlan = { started: true, ready: true };
    },

    async stop(context) {
      if (state.requestFrameId !== null) {
        cancelAnimationFrame(state.requestFrameId);
        state.requestFrameId = null;
      }

      state.subscriptions.forEach((sub) => sub?.unsubscribe?.());
      state.subscriptions = [];

      state.mapEventUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
      state.mapEventUnsubscribers = [];

      state.renderer?.detachAllOverlays();
      state.renderer = null;
      state.store$ = null;
      state.mapFacade = null;
      state.started = false;
      state.sitePlans = {};
      state.sitePlanValues = [];
      state.resultCommunityIds = new Set();
      state.lastRenderedSnapshot = null;

      delete context.state.sitePlan;
    },
  };
}
